import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTheme } from "./ThemeContext";
import { useAuth } from "./AuthContext";
import { useCartStore } from "./store/cartStore";
import { useTranslation } from "react-i18next";

import LanguageSwitcher from "./components/LanguageSwitcher";
import { 
  ShoppingBagIcon, 
  SunIcon, 
  MoonIcon, 
  MenuIcon, 
  CloseIcon, 
  ChevronDownIcon
} from "./components/NavIcons";

export default function Navbar() {
  const { isDarkMode, toggleTheme } = useTheme();
  const { user, logout, openLoginModal } = useAuth();
  const cartItems = useCartStore((state) => state.items);
  const { t } = useTranslation();
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Scroll detection
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Timeout refs for dropdowns
  const moreMenuTimeoutRef = React.useRef(null);
  const userMenuTimeoutRef = React.useRef(null);

  const handleMoreEnter = () => {
    if (moreMenuTimeoutRef.current) clearTimeout(moreMenuTimeoutRef.current);
    setIsMoreOpen(true);
  };

  const handleMoreLeave = () => {
    moreMenuTimeoutRef.current = setTimeout(() => {
      setIsMoreOpen(false);
    }, 200);
  };

  const handleUserEnter = () => {
    if (userMenuTimeoutRef.current) clearTimeout(userMenuTimeoutRef.current);
    setIsUserMenuOpen(true);
  };

  const handleUserLeave = () => {
    userMenuTimeoutRef.current = setTimeout(() => {
      setIsUserMenuOpen(false);
    }, 200);
  };

  return (
    <>
      <nav 
        className={`
          fixed top-0 left-0 right-0 z-50
          bg-raya-bg dark:bg-raya-bg
          border-b border-raya-border
          transition-all duration-300
          ${scrolled ? 'py-2 shadow-lg shadow-black/5' : 'py-4'}
        `}
        aria-label="Main navigation"
      >
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center group"
            >
              {/* Desktop Logo */}
              <span className={`
                hidden md:block font-serif font-bold tracking-wide
                bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600
                bg-clip-text text-transparent
                transition-all duration-300
                ${scrolled ? 'text-2xl' : 'text-3xl'}
              `}>
                RayaElegance
              </span>
              
              {/* Mobile Logo Icon */}
              <div className="md:hidden flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-r from-yellow-400 to-yellow-600" aria-label="Raya Elegance Logo">
                <span className="text-white font-serif font-bold text-xl">RE</span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {/* Primary Nav Links */}
              <Link
                to="/"
                className={`nav-link font-medium transition-all duration-300 transform hover:scale-105 ${location.pathname === '/' ? 'text-raya-accent' : 'text-raya-text-secondary dark:text-raya-text-primary hover:text-raya-accent dark:hover:text-raya-accent'}`}
              >
                {t('navbar.home')}
              </Link>
              <Link
                to="/products"
                className={`nav-link font-medium transition-all duration-300 transform hover:scale-105 ${location.pathname === '/products' ? 'text-raya-accent' : 'text-raya-text-secondary dark:text-raya-text-primary hover:text-raya-accent dark:hover:text-raya-accent'}`}
              >
                {t('navbar.products')}
              </Link>
              <Link
                to="/about"
                className={`nav-link font-medium transition-all duration-300 transform hover:scale-105 ${location.pathname === '/about' ? 'text-raya-accent' : 'text-raya-text-secondary dark:text-raya-text-primary hover:text-raya-accent dark:hover:text-raya-accent'}`}
              >
                {t('navbar.about')}
              </Link>
              <Link
                to="/contact"
                className={`nav-link font-medium transition-all duration-300 transform hover:scale-105 ${location.pathname === '/contact' ? 'text-raya-accent' : 'text-raya-text-secondary dark:text-raya-text-primary hover:text-raya-accent dark:hover:text-raya-accent'}`}
              >
                {t('navbar.contact')}
              </Link>

              {/* More Dropdown */}
              <div 
                className="relative"
                onMouseEnter={handleMoreEnter}
                onMouseLeave={handleMoreLeave}
              >
                <button 
                  className="nav-link flex items-center gap-1 text-raya-text-secondary dark:text-raya-text-primary hover:text-raya-accent dark:hover:text-raya-accent font-medium transition-all duration-300"
                  aria-expanded={isMoreOpen}
                  aria-haspopup="true"
                >
                  {t('navbar.more')}
                  <ChevronDownIcon className={`transition-transform duration-300 ${isMoreOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isMoreOpen && (
                  <div className="absolute top-full left-0 mt-2 w-56 bg-raya-card dark:bg-raya-card rounded-xl shadow-2xl shadow-black/10 border border-raya-border dark:border-raya-border overflow-hidden animate-dropdown-enter">
                    <Link
                      to="/dietary-info"
                      className="block px-4 py-3 hover:bg-raya-accent-light hover:text-yellow-600 dark:hover:text-yellow-400 transition-all duration-200 transform hover:translate-x-1"
                    >
                      {t('navbar.dietaryInfo')}
                    </Link>
                    <Link
                      to="/faq"
                      className="block px-4 py-3 hover:bg-raya-accent-light hover:text-yellow-600 dark:hover:text-yellow-400 transition-all duration-200 transform hover:translate-x-1"
                    >
                      {t('navbar.support')}
                    </Link>
                    <Link
                      to="/shipping"
                      className="block px-4 py-3 hover:bg-raya-accent-light hover:text-yellow-600 dark:hover:text-yellow-400 transition-all duration-200 transform hover:translate-x-1"
                    >
                      {t('navbar.shipping')}
                    </Link>
                    <Link
                      to="/returns"
                      className="block px-4 py-3 hover:bg-raya-accent-light hover:text-yellow-600 dark:hover:text-yellow-400 transition-all duration-200 transform hover:translate-x-1"
                    >
                      {t('navbar.returns')}
                    </Link>
                    
                    {/* Policies Links */}
                    <div className="border-t border-gray-200 dark:border-slate-800 mt-1 pt-1">
                      <Link
                        to="/privacy"
                        className="block px-4 py-3 hover:bg-raya-accent-light hover:text-yellow-600 dark:hover:text-yellow-400 transition-all duration-200 transform hover:translate-x-1"
                      >
                        {t('navbar.privacy')}
                      </Link>
                      <Link
                        to="/terms"
                        className="block px-4 py-3 hover:bg-raya-accent-light hover:text-yellow-600 dark:hover:text-yellow-400 transition-all duration-200 transform hover:translate-x-1"
                      >
                        {t('navbar.terms')}
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Side Icons & Auth */}
            <div className="flex items-center gap-3">
              {/* Language Switcher */}
              <LanguageSwitcher />
              
              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-full bg-raya-card hover:bg-raya-accent-light transition-all duration-300 transform hover:scale-110 hover:rotate-12"
                aria-label="Toggle theme"
              >
                <div className="transition-transform duration-300">
                  {isDarkMode ? <SunIcon className="text-raya-accent w-5 h-5" /> : <MoonIcon className="text-raya-text-secondary dark:text-raya-text-primary w-5 h-5" />}
                </div>
              </button>

              {/* Cart */}
              <Link to="/cart" className="relative group">
                <button className="p-2 rounded-full bg-raya-card hover:bg-raya-accent-light transition-all duration-300 transform hover:scale-110">
                  <ShoppingBagIcon className="text-raya-text-secondary dark:text-raya-text-primary group-hover:text-raya-accent dark:group-hover:text-raya-accent transition-colors w-6 h-6" />
                </button>
                {cartItems.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-gradient-to-r from-red-500 to-pink-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold animate-pulse">
                    {cartItems.length}
                  </span>
                )}
              </Link>

              {/* Desktop Auth */}
              <div className="hidden md:flex items-center gap-3">
                {user ? (
                  <div 
                    className="relative"
                    onMouseEnter={handleUserEnter}
                    onMouseLeave={handleUserLeave}
                  >
                    <button 
                      className="flex items-center gap-2 p-2 rounded-full hover:bg-raya-accent-light transition-all"
                      aria-expanded={isUserMenuOpen}
                      aria-haspopup="true"
                    >
                      <div className="w-8 h-8 rounded-full bg-gradient-to-r from-yellow-400 to-yellow-600 flex items-center justify-center text-white font-bold text-sm">
                        {user.email[0].toUpperCase()}
                      </div>
                    </button>
                    
                    {isUserMenuOpen && (
                      <div className="absolute top-full right-0 mt-2 w-48 bg-raya-card rounded-xl shadow-2xl shadow-black/10 border border-raya-border overflow-hidden">
                        <div className="px-4 py-3 border-b border-raya-border">
                          <p className="text-sm font-medium truncate">{user.email}</p>
                        </div>
                        <Link
                          to="/my-orders"
                          className="block px-4 py-3 hover:bg-raya-accent-light hover:text-yellow-600 dark:hover:text-yellow-400 transition-all"
                        >
                          {t('navbar.myOrders')}
                        </Link>
                        {user.isAdmin && (
                          <Link
                            to="/admin"
                            className="block px-4 py-3 hover:bg-purple-50 dark:hover:bg-purple-900/20 hover:text-purple-600 dark:hover:text-purple-400 transition-all"
                          >
                            {t('navbar.admin')}
                          </Link>
                        )}
                        <button
                          onClick={logout}
                          className="w-full text-left px-4 py-3 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 transition-all"
                        >
                          {t('navbar.logout')}
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <button 
                    onClick={openLoginModal}
                    className="px-6 py-2 rounded-full bg-gradient-to-r from-raya-accent to-raya-accent-hover hover:from-raya-accent-hover hover:to-raya-accent text-raya-bg font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                  >
                    {t('navbar.login')}
                  </button>
                )}
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 rounded-full bg-raya-card hover:bg-raya-accent-light transition-all"
                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={isMobileMenuOpen}
              >
                {isMobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 z-40 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          
          
          <div 
            className="absolute right-0 top-0 bottom-0 w-80 max-w-[85%] bg-raya-card shadow-2xl transform transition-transform duration-300 ease-out overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 space-y-4">
              {/* Close Button */}
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="absolute top-4 right-4 p-2 rounded-full hover:bg-raya-section dark:hover:bg-raya-section"
              >
                <CloseIcon />
              </button>

              {/* User Info */}
              {user && (
                <div className="pb-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-yellow-400 to-yellow-600 flex items-center justify-center text-white font-bold">
                      {user.email[0].toUpperCase()}
                    </div>
                    <span className="text-sm font-medium truncate">{user.email}</span>
                  </div>
                </div>
              )}

              {/* Primary Links */}
              <div className="space-y-2">
                <Link
                  to="/"
                  className="block py-3 px-4 rounded-lg hover:bg-yellow-50 dark:hover:bg-gray-800 hover:text-yellow-600 dark:hover:text-yellow-400 font-medium transition-all"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t('navbar.home')}
                </Link>
                <Link
                  to="/products"
                  className="block py-3 px-4 rounded-lg hover:bg-yellow-50 dark:hover:bg-gray-800 hover:text-yellow-600 dark:hover:text-yellow-400 font-medium transition-all"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t('navbar.products')}
                </Link>
                <Link
                  to="/my-orders"
                  className="block py-3 px-4 rounded-lg hover:bg-yellow-50 dark:hover:bg-gray-800 hover:text-yellow-600 dark:hover:text-yellow-400 font-medium transition-all"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t('navbar.myOrders')}
                </Link>
                <Link
                  to="/about"
                  className="block py-3 px-4 rounded-lg hover:bg-yellow-50 dark:hover:bg-gray-800 hover:text-yellow-600 dark:hover:text-yellow-400 font-medium transition-all"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t('navbar.about')}
                </Link>
                <Link
                  to="/contact"
                  className="block py-3 px-4 rounded-lg hover:bg-yellow-50 dark:hover:bg-gray-800 hover:text-yellow-600 dark:hover:text-yellow-400 font-medium transition-all"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t('navbar.contact')}
                </Link>
              </div>

              {/* Divider */}
              <div className="border-t border-yellow-200 dark:border-yellow-900/30" />

              {/* Secondary Links */}
              <div className="space-y-2">
                <Link
                  to="/dietary-info"
                  className="block py-3 px-4 rounded-lg hover:bg-yellow-50 dark:hover:bg-gray-800 hover:text-yellow-600 dark:hover:text-yellow-400 transition-all"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t('navbar.dietaryInfo')}
                </Link>
                <Link
                  to="/faq"
                  className="block py-3 px-4 rounded-lg hover:bg-yellow-50 dark:hover:bg-gray-800 hover:text-yellow-600 dark:hover:text-yellow-400 transition-all"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t('navbar.support')}
                </Link>
                <Link
                  to="/shipping"
                  className="block py-3 px-4 rounded-lg hover:bg-yellow-50 dark:hover:bg-gray-800 hover:text-yellow-600 dark:hover:text-yellow-400 transition-all"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t('navbar.shipping')}
                </Link>
                <Link
                  to="/returns"
                  className="block py-3 px-4 rounded-lg hover:bg-yellow-50 dark:hover:bg-gray-800 hover:text-yellow-600 dark:hover:text-yellow-400 transition-all"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t('navbar.returns')}
                </Link>
                <Link
                  to="/privacy"
                  className="block py-3 px-4 rounded-lg hover:bg-yellow-50 dark:hover:bg-gray-800 hover:text-yellow-600 dark:hover:text-yellow-400 transition-all"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t('navbar.privacy')}
                </Link>
                <Link
                  to="/terms"
                  className="block py-3 px-4 rounded-lg hover:bg-yellow-50 dark:hover:bg-gray-800 hover:text-yellow-600 dark:hover:text-yellow-400 transition-all"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t('navbar.terms')}
                </Link>
              </div>

              {/* Auth Buttons */}
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700 space-y-2">
                {user ? (
                  <>
                    {user.isAdmin && (
                      <Link to="/admin" onClick={() => setIsMobileMenuOpen(false)}>
                        <button className="w-full px-6 py-3 rounded-full bg-purple-600 hover:bg-purple-700 text-white font-semibold transition-all">
                          {t('navbar.admin')}
                        </button>
                      </Link>
                    )}
                    <button
                      onClick={() => {
                        logout();
                        setIsMobileMenuOpen(false);
                      }}
                      className="w-full px-6 py-3 rounded-full bg-red-500 hover:bg-red-600 text-white font-semibold transition-all"
                    >
                      {t('navbar.logout')}
                    </button>
                  </>
                ) : (
                  <button 
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      openLoginModal();
                    }}
                    className="w-full px-6 py-3 rounded-full bg-gradient-to-r from-yellow-400 to-yellow-600 hover:from-yellow-500 hover:to-yellow-700 text-white font-semibold shadow-lg transition-all"
                  >
                    {t('navbar.login')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add necessary CSS */}
    </>
  );
}
